import { datadogRum } from '@datadog/browser-rum';

export const formatFilePath = (url: string): string => {
  return url.replace(/%2F/g, '/');
};

/**
 * Median's native bridge fetches URLs outside the WebView; blob URLs only
 * exist in the WebView and must use an in-page download (anchor) instead.
 * Data URLs are self-contained and can be handled by the native bridge.
 */
export const canUseMedianNativeDownload = (url: string): boolean => {
  try {
    const resolved = new URL(url, window.location.href);
    return (
      resolved.protocol === 'http:' ||
      resolved.protocol === 'https:' ||
      resolved.protocol === 'data:'
    );
  } catch {
    return false;
  }
};

type DecodingType = 'full' | 'paths-only';

/**
 * Open file in a new tab with handling of decoding URI and
 * support for download on native device
 *
 * When decoding is set to 'paths-only' it will only decode the '/' of directory
 * paths while leaving all other special characters intact.
 *
 * When decoding is set to 'full' it will decode the entire url.
 */
export const openUrlInNewTab = (
  url?: string | null,
  options?: { decoding?: DecodingType; onFail?: () => void },
) => {
  const { decoding = 'full', onFail } = options ?? {};

  try {
    if (!url) {
      datadogRum.addAction(
        'Unable to open URL in new tab because URL is null',
        {
          url,
          decoding,
        },
      );
      onFail?.();
      return;
    }

    const parsedUrl =
      decoding === 'paths-only' ? formatFilePath(url) : decodeURIComponent(url);
    if (
      window.median?.share?.downloadFile &&
      canUseMedianNativeDownload(parsedUrl)
    ) {
      datadogRum.addAction('Opened File In New Tab With Median', {
        url: parsedUrl,
        decoding,
      });
      window.median.share.downloadFile({
        url: parsedUrl,
        open: true,
      });
      return;
    }

    datadogRum.addAction('Opened File In New Tab With Window.Open', {
      url: parsedUrl,
      decoding,
    });
    window.open(parsedUrl, '_blank');
  } catch (error) {
    datadogRum.addError(error, {
      url,
      decoding,
      message: 'Unable to open URL in new tab',
    });
    onFail?.();
  }
};
